import { formatMoney, parseIso } from '@fin/shared';

export { formatMoney };

/** «₽» money; kopecks only when the sum has them */
export const rub = (v: number | null | undefined, opts?: { sign?: boolean }) => formatMoney(v, 'RUB', opts);
export const money = (v: number | null | undefined, currency: string, opts?: { sign?: boolean; decimals?: number }) => formatMoney(v, currency, opts);

const monthsGen = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const monthsNom = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const monthsShort = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const weekdays = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];
const weekdaysShort = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

export const monthName = (m: number) => monthsNom[m];
const monthsShortNom = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
export const monthShort = (m: number) => monthsShortNom[m];

/** 20 октября 2026 (year omitted when current) */
export function dateLong(iso: string, withYear?: boolean) {
  const d = parseIso(iso);
  const y = d.getUTCFullYear();
  const showYear = withYear ?? y !== new Date().getFullYear();
  return `${d.getUTCDate()} ${monthsGen[d.getUTCMonth()]}${showYear ? ` ${y}` : ''}`;
}

export function dateShort(iso: string) {
  const d = parseIso(iso);
  return `${d.getUTCDate()} ${monthsShort[d.getUTCMonth()]}`;
}

export function weekday(iso: string, short = false) {
  const d = parseIso(iso).getUTCDay();
  return short ? weekdaysShort[d] : weekdays[d];
}

/** «10 – 24 октября» or «25 октября – 9 ноября» */
export function rangeLabel(start: string, end: string) {
  const a = parseIso(start), b = parseIso(end);
  const sameMonth = a.getUTCMonth() === b.getUTCMonth() && a.getUTCFullYear() === b.getUTCFullYear();
  const yr = b.getUTCFullYear() !== new Date().getFullYear() ? ` ${b.getUTCFullYear()}` : '';
  return sameMonth
    ? `${a.getUTCDate()} – ${b.getUTCDate()} ${monthsGen[b.getUTCMonth()]}${yr}`
    : `${a.getUTCDate()} ${monthsGen[a.getUTCMonth()]} – ${b.getUTCDate()} ${monthsGen[b.getUTCMonth()]}${yr}`;
}

export const pct = (v: number, digits = 0) => `${(v * 100).toFixed(digits).replace('.', ',')}%`;

export function plural(n: number, one: string, few: string, many: string) {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

/** parse a user-typed number: «1 234,5» → 1234.5 */
export function parseNum(s: string): number | null {
  const t = s.replace(/\s/g, '').replace(',', '.');
  if (t === '' || t === '-') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export const numInput = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v).replace('.', ','));

export const walletTypeLabels: Record<string, string> = {
  bank: 'Банк', cash: 'Наличные', crypto: 'Крипта', savings: 'Копилка', investment: 'Инвестиции', other: 'Другое',
};

export const kindLabels: Record<string, string> = {
  expense: 'Расход', income: 'Доход', debt: 'Взял в долг', repayment: 'Вернул долг',
  loan: 'Дал в долг', debt_collection: 'Мне вернули', transfer: 'Перевод',
};

export const priorityLabels: Record<number, string> = { 1: 'Срочно', 2: 'Важно', 3: 'Обычное', 4: 'Подождёт', 5: 'Не нужно' };
